import { format, isValid } from 'date-fns';
import { dateLocales } from '@/i18n/dateLocales';
import type { Purchase } from '@/api/types';

/**
 * Visningshjälpare för barnets köp ("Mina köp" + kvittot).
 *
 * Två saker görs DEFENSIVT här, med avsikt:
 *
 * 1. Datum. `Purchase.createdAt`/`resolvedAt` typas som `Date`, men backend skickar
 *    en Timestamp-sträng. `parseDates` i api/runtime.ts konverterar bara värden som
 *    matchar det strikta ISO-mönstret — ett format som `"2026-08-23 12:00:00"` slinker
 *    igenom som sträng. `format()` på en sträng KRASCHAR. Därför normaliserar vi alltid
 *    via `toValidDate` och renderar ingen datumrad alls när datumet inte går att tolka.
 *
 * 2. `rejectionNote` är fritext skriven av en vuxen. Den trimmas och trunkeras innan
 *    den visas för barnet — en vägg av text i barnvyn är ett tillgänglighetsproblem.
 *
 * Inga relativa tidsuttryck ("för 2 dagar sedan"): NPF-barnvyn visar konkret veckodag
 * + datum. Formateringen går via projektets `dateLocales` (date-fns).
 */

/** Max antal tecken av en vuxens `rejectionNote` som visas för barnet. */
export const REJECTION_NOTE_MAX_LENGTH = 140;

/** Antal poster i "Klart"-sektionen som visas innan "Visa fler". */
export const DONE_SECTION_PAGE_SIZE = 5;

/**
 * Normaliserar ett datumfält från API:et till ett giltigt `Date` — eller `null`.
 * Accepterar `Date`, ISO-/Timestamp-sträng och epoch-tal. `null` betyder "rendera
 * ingen datumrad", aldrig "krascha".
 */
export const toValidDate = (value: Date | string | number | null | undefined): Date | null => {
  if (value === null || value === undefined) return null;
  const date = value instanceof Date ? value : new Date(value);
  return isValid(date) ? date : null;
};

/**
 * Formaterar ett köpdatum som konkret veckodag + datum, t.ex. "lördag 23 augusti".
 * Returnerar `null` när datumet inte går att tolka, så att anropande vy kan utelämna
 * hela raden istället för att visa "Invalid Date".
 */
export const formatPurchaseDate = (
  value: Date | string | number | null | undefined,
  lang: string,
): string | null => {
  const date = toValidDate(value);
  if (!date) return null;
  // `i18n.language` är 'sv-SE' för en användare som aldrig öppnat språkväljaren
  // (LanguageDetector läser navigator, och i18n.ts sätter varken load: 'languageOnly'
  // eller supportedLngs). dateLocales är nycklad på 'sv' → utan split hamnar ett
  // svenskt barn på enUS och får "Saturday 23 August" i kvittot. Samma mönster som
  // ChildWeek.tsx/TeenWeek.tsx/WeekPage.tsx.
  const locale = dateLocales[lang.split('-')[0]] ?? dateLocales.en;
  return format(date, 'EEEE d MMMM', { locale });
};

/**
 * Trimmar och trunkerar en vuxens avslagsnotis. Returnerar `null` för tom/blank
 * notis så att fältet utelämnas helt (aldrig en tom etikett utan innehåll).
 */
export const cleanRejectionNote = (note: string | null | undefined): string | null => {
  if (typeof note !== 'string') return null;
  const trimmed = note.trim();
  if (trimmed.length === 0) return null;
  if (trimmed.length <= REJECTION_NOTE_MAX_LENGTH) return trimmed;
  return `${trimmed.slice(0, REJECTION_NOTE_MAX_LENGTH).trimEnd()}…`;
};

/**
 * Nyast först på den tidpunkt sektionen FAKTISKT visar. "Klart"-korten visar
 * `resolvedAt` ("Klart lördag 23 augusti"), så de måste sorteras på det — annars
 * hamnar ett köp som gjordes för tre veckor sedan men löstes in idag under ett som
 * gjordes igår, och sektionen ser osorterad ut för barnet. `createdAt` är fallback
 * för poster som saknar `resolvedAt`.
 */
export const byResolvedNewestFirst = (a: Purchase, b: Purchase): number => {
  const at = toValidDate(a.resolvedAt)?.getTime() ?? toValidDate(a.createdAt)?.getTime();
  const bt = toValidDate(b.resolvedAt)?.getTime() ?? toValidDate(b.createdAt)?.getTime();
  if (at === undefined && bt === undefined) return 0;
  if (at === undefined) return 1;
  if (bt === undefined) return -1;
  return bt - at;
};

/** Nyast först. Sorterar på tolkat `createdAt`; otolkbara datum hamnar sist. */
export const byNewestFirst = (a: Purchase, b: Purchase): number => {
  const aTime = toValidDate(a.createdAt)?.getTime();
  const bTime = toValidDate(b.createdAt)?.getTime();
  if (aTime === undefined && bTime === undefined) return 0;
  if (aTime === undefined) return 1;
  if (bTime === undefined) return -1;
  return bTime - aTime;
};

/**
 * Delar upp köpen i vyns TVÅ sektioner. Medvetet INTE en kronologisk historik:
 * blandas statusarna försvinner det som väntar på en vuxen i bruset, och då är
 * funktionen värdelös. `pending` först, alltid.
 */
export const splitPurchases = (
  purchases: Purchase[],
): { pending: Purchase[]; done: Purchase[] } => ({
  // `done` är KOMPLEMENTET till pending, inte en vitlista av kända statusar. Med en
  // vitlista skulle ett köp med okänd status (ny status i backend) hamna i ingen
  // sektion och försvinna helt ur vyn — tomvyn visas bara vid noll köp, så barnet
  // hade fått en rubrik utan innehåll och ingen väg vidare. Objektspermanens är ett
  // absolut krav: renderDoneCard behandlar allt icke-fulfilled som den neutrala
  // varianten, så sämsta utfall blir korrekt i stället för tomt.
  pending: purchases.filter((purchase) => purchase.status === 'pending').sort(byNewestFirst),
  done: purchases.filter((purchase) => purchase.status !== 'pending').sort(byResolvedNewestFirst),
});
